import { useMedplum } from '@medplum/react';
import type { MedplumClient } from '@medplum/core';
import type { EpisodeOfCare, Patient, Practitioner, RelatedPerson } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { buildPractitionerNameMap, mapPilotPatientToClient } from '../lib/fhirCaseloadMapping';
import {
  chartScopeFromRoleAndPractitioner,
  filterEpisodeOfCareForCaseload,
  supervisorCaseloadCareManagerSearchIds,
  type ChartScopeContext,
} from '../lib/rbac/eocChartScope';
import { useSupervisedPractitionerIds } from './useSupervisedPractitionerIds';
import { getViewScope } from '../lib/rbac/scopeAccess';
import type { Client, ClientGroup } from '../pages/caseload/data';
import {
  mergeCaseloadDirectoryClients,
  shouldMergeDirectoryOnlyCaseloadClients,
} from '../pages/caseload/data/caseloadUtils';
import { useCurrentUserPractitioner } from './useCurrentUserPractitioner';
import { PARTICIPANTS_EXT } from '@unify/fhir-model/constants';
import {
  codeFromServiceTypeCodeableConcept,
  displayLabelForEpisodeType,
} from '../lib/fhirValueSets/sessionAndEpisodeTypes';
import { syncEpisodeTypeCatalogLabels } from '../pages/calendar/episodeOfCareTypeCatalog';

const GROUP_EPISODE_CODES = new Set(['couples', 'family']);

function patientIdFromEpisode(eoc: EpisodeOfCare): string | undefined {
  const ref = eoc.patient?.reference;
  if (!ref) return undefined;
  if (ref.startsWith('Patient/')) return ref.slice('Patient/'.length);
  const idx = ref.lastIndexOf('/');
  return idx >= 0 ? ref.slice(idx + 1) : undefined;
}

/** EpisodeOfCare rows for the caseload viewer (primary filter: care-manager). */
export async function fetchCaseloadEpisodes(
  medplum: MedplumClient,
  chartCtx: ChartScopeContext,
): Promise<EpisodeOfCare[]> {
  const scope = chartCtx.viewScope;

  if (scope === 'ALL' || scope === 'PRACTICE' || scope === 'CUSTOM') {
    return medplum.searchResources('EpisodeOfCare', { _count: '500' });
  }

  if (scope === 'TEAM' && chartCtx.role === 'supervisor') {
    const searchIds = supervisorCaseloadCareManagerSearchIds(chartCtx);
    const batches = await Promise.all(
      searchIds.map((pid) =>
        medplum.searchResources('EpisodeOfCare', {
          'care-manager': `Practitioner/${pid}`,
          _count: '500',
        }),
      ),
    );
    const byId = new Map<string, EpisodeOfCare>();
    for (const eoc of batches.flat()) {
      if (eoc.id) byId.set(eoc.id, eoc);
    }
    return [...byId.values()];
  }

  const pid = chartCtx.practitionerId;
  if (!pid) return [];

  return medplum.searchResources('EpisodeOfCare', {
    'care-manager': `Practitioner/${pid}`,
    _count: '500',
  });
}

function groupEpisodesByPatientId(eocs: EpisodeOfCare[]): Map<string, EpisodeOfCare[]> {
  const byPatient = new Map<string, EpisodeOfCare[]>();
  for (const eoc of eocs) {
    const patientId = patientIdFromEpisode(eoc);
    if (!patientId) continue;
    const list = byPatient.get(patientId) ?? [];
    list.push(eoc);
    byPatient.set(patientId, list);
  }
  return byPatient;
}

/**
 * Build dynamic ClientGroup objects from FHIR episodes and related persons.
 * Detects couples/family therapy EoCs and matches RelatedPersons + Patients
 * to populate groups and assign group IDs to clients.
 *
 * Only considers EoCs the viewer may see on caseload (same rule as
 * {@link filterEpisodeOfCareForCaseload}) so another provider's couples/family
 * episode is not merged onto the client's row.
 */
function buildGroupsFromFhir(
  patients: Patient[],
  allEocs: Map<string, EpisodeOfCare[]>,
  allRelatedPersons: Map<string, RelatedPerson[]>,
  clients: Client[],
  chartCtx: ChartScopeContext,
): ClientGroup[] {
  const groups: ClientGroup[] = [];
  const clientById = new Map(clients.map((c) => [c.id, c]));
  const patientById = new Map(patients.map((p) => [p.id ?? '', p]));

  for (const [patientId, eocs] of allEocs) {
    const visibleForCaseload = filterEpisodeOfCareForCaseload(eocs, chartCtx);
    const visibleEocIds = new Set(
      visibleForCaseload.map((e) => e.id).filter((id): id is string => Boolean(id)),
    );

    for (const eoc of eocs) {
      if (!eoc.id || !visibleEocIds.has(eoc.id)) continue;

      const episodeCode = codeFromServiceTypeCodeableConcept(eoc.type?.[0]);
      if (!episodeCode || !GROUP_EPISODE_CODES.has(episodeCode)) continue;

      const episodeName = displayLabelForEpisodeType(eoc.type?.[0]);

      const groupId = `fhir-group-${eoc.id}`;
      if (groups.some((g) => g.id === groupId)) continue;

      const patient = patientById.get(patientId);
      if (!patient) continue;

      const isCouples = episodeCode === 'couples';
      const members: ClientGroup['members'] = [
        { clientId: patientId, clientName: clientById.get(patientId)?.name ?? 'Patient', role: isCouples ? 'Partner' : 'Child' },
      ];

      const eocParticipantRefs = new Set(
        (eoc.extension ?? [])
          .filter((e) => e.url === PARTICIPANTS_EXT && e.valueReference?.reference)
          .map((e) => e.valueReference!.reference!),
      );
      const relatedPersons = (allRelatedPersons.get(patientId) ?? []).filter(
        (rp) => rp.id && eocParticipantRefs.has(`RelatedPerson/${rp.id}`),
      );
      for (const rp of relatedPersons) {
        const rpName = rp.name?.[0]
          ? [...(rp.name[0].given ?? []), rp.name[0].family].filter(Boolean).join(' ')
          : 'Related';
        const rpRelCode = rp.relationship?.[0]?.coding?.[0]?.code;
        const role = isCouples
          ? 'Partner'
          : rpRelCode === 'FTH' || rpRelCode === 'MTH' || rpRelCode === 'PRN'
            ? 'Parent'
            : rpRelCode === 'SPS'
              ? 'Spouse'
              : 'Member';

        const matchingClient = clients.find(
          (c) => c.name.toLowerCase() === rpName.toLowerCase(),
        );
        const memberId = matchingClient?.id ?? `rp-${rp.id}`;
        if (!members.some((m) => m.clientId === memberId)) {
          members.push({ clientId: memberId, clientName: rpName, role });
        }
      }

      const provider =
        eoc.careManager?.display ?? 'Provider';

      groups.push({
        id: groupId,
        name: isCouples
          ? `${patient.name?.[0]?.family ?? ''} Couples Therapy`
          : `${patient.name?.[0]?.family ?? ''} Family Therapy`,
        type: isCouples ? 'Couples Therapy' : 'Family Therapy',
        members,
        facilitators: [provider],
        status: 'Active',
        startDate: eoc.period?.start?.slice(0, 10),
      });

      for (const m of members) {
        const c = clientById.get(m.clientId);
        if (c) {
          c.groups = c.groups ?? [];
          if (!c.groups.includes(groupId)) c.groups.push(groupId);
          if (!c.episodes?.some((ep) => ep.name === episodeName)) {
            c.episodes = c.episodes ?? [];
            c.episodes.push({
              id: eoc.id ?? groupId,
              name: episodeName,
              status: (eoc.status as 'active') ?? 'active',
              startDate: eoc.period?.start?.slice(0, 10) ?? '',
              provider,
            });
          }
        }
      }
    }
  }
  return groups;
}

/**
 * Loads caseload clients from Medplum EpisodeOfCare resources where the viewer is care manager
 * (or broader scope for admin/supervisor roles).
 */
export function useMedplumCaseloadClients(): {
  clients: Client[];
  /** Patients for the Clients tab — episode-connected for OWN/TEAM; may include directory-only for PRACTICE+. */
  allClients: Client[];
  groups: ClientGroup[];
  loading: boolean;
  error: Error | null;
  reload: () => void;
} {
  const medplum = useMedplum();
  const rbac = usePermissions();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const { ids: supervisedPractitionerIds, loading: superviseesLoading } = useSupervisedPractitionerIds();
  const viewerPractitionerId = practitioner?.id;
  const viewerIsSupervisor = rbac.ready && rbac.role === 'supervisor';
  const viewScope = rbac.ready ? getViewScope(rbac.role) : 'OWN';
  const needsPractitionerForCaseload = viewScope === 'OWN' && rbac.role === 'provider';

  const [clients, setClients] = useState<Client[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [groups, setGroups] = useState<ClientGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!rbac.ready) {
        return;
      }
      // Caseload is provider/staff only — skip for client portal accounts.
      if (rbac.isClient) {
        setClients([]);
        setAllClients([]);
        setGroups([]);
        setLoading(false);
        setError(null);
        return;
      }
      if ((needsPractitionerForCaseload && practitionerLoading) || superviseesLoading) {
        setLoading(true);
        return;
      }
      try {
        setLoading(true);
        medplum.invalidateSearches('EpisodeOfCare');
        medplum.invalidateSearches('Patient');
        await syncEpisodeTypeCatalogLabels(medplum).catch((err) => {
          console.warn('[caseload] Episode-of-care type catalog load failed; using static labels.', err);
        });
        const chartCtx = chartScopeFromRoleAndPractitioner(
          rbac.role,
          viewerPractitionerId,
          supervisedPractitionerIds,
        );

        const eocsForCaseload = await fetchCaseloadEpisodes(medplum, chartCtx);
        const eocsByPatient = groupEpisodesByPatientId(eocsForCaseload);

        let practitioners: Practitioner[] = [];
        try {
          practitioners = await medplum.searchResources('Practitioner', { _count: '200' });
        } catch (err) {
          console.warn(
            '[caseload] Practitioner search failed (403 usually means AccessPolicy lacks Practitioner read).',
            err,
          );
        }
        const prMap = buildPractitionerNameMap(practitioners);

        const allEocsForGrouping = new Map<string, EpisodeOfCare[]>();
        const authorizedEocIdsByPatient = new Map<string, Set<string>>();
        const allRelatedPersons = new Map<string, RelatedPerson[]>();
        const rows: Client[] = [];
        const caseloadPatients: Patient[] = [];

        for (const [patientId, eocsRaw] of eocsByPatient) {
          const eocs = filterEpisodeOfCareForCaseload(eocsRaw, chartCtx);
          if (eocs.length === 0) continue;

          authorizedEocIdsByPatient.set(
            patientId,
            new Set(eocs.map((e) => e.id).filter((id): id is string => Boolean(id))),
          );
          allEocsForGrouping.set(patientId, eocs);

          const pref = `Patient/${patientId}`;
          let patient: Patient;
          try {
            patient = await medplum.readResource('Patient', patientId);
          } catch (err) {
            console.warn(`[caseload] Patient/${patientId} read failed, skipping:`, err);
            continue;
          }
          const [appts, rps] = await Promise.all([
            medplum.searchResources('Appointment', { actor: pref }),
            medplum.searchResources('RelatedPerson', { patient: pref }).catch(() => [] as RelatedPerson[]),
          ]);
          allRelatedPersons.set(patientId, rps);
          caseloadPatients.push(patient);
          rows.push(
            mapPilotPatientToClient(patient, eocs, appts, prMap, {
              viewerPractitionerId,
              viewerIsSupervisor,
            }),
          );
        }

        const dynamicGroups = buildGroupsFromFhir(
          caseloadPatients,
          allEocsForGrouping,
          allRelatedPersons,
          rows,
          chartCtx,
        );

        for (const c of rows) {
          const auth = authorizedEocIdsByPatient.get(c.id);
          if (auth && c.episodes?.length) {
            c.episodes = c.episodes.filter((ep) => auth.has(ep.id));
          }
        }

        rows.sort((a, b) => a.name.localeCompare(b.name));

        let mergedClients = rows;
        if (shouldMergeDirectoryOnlyCaseloadClients(chartCtx.viewScope)) {
          const episodeClientIds = new Set(rows.map((client) => client.id));
          let directoryOnlyClients: Client[] = [];
          try {
            const allPatients = await medplum.searchResources('Patient', {
              _count: '500',
              _sort: 'name',
            });
            directoryOnlyClients = allPatients
              .filter((patient) => patient.id && !episodeClientIds.has(patient.id))
              .map((patient) =>
                mapPilotPatientToClient(patient, [], [], prMap, {
                  viewerPractitionerId,
                  viewerIsSupervisor,
                }),
              );
          } catch (err) {
            console.warn('[caseload] Patient directory search failed; showing episode clients only.', err);
          }
          mergedClients = mergeCaseloadDirectoryClients(rows, directoryOnlyClients);
        }

        if (!cancelled) {
          setClients(rows);
          setAllClients(mergedClients);
          setGroups(dynamicGroups);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    medplum,
    reloadToken,
    viewerPractitionerId,
    viewerIsSupervisor,
    rbac.ready,
    rbac.isClient,
    rbac.role,
    needsPractitionerForCaseload,
    practitionerLoading,
    superviseesLoading,
    supervisedPractitionerIds,
  ]);

  return { clients, allClients, groups, loading, error, reload };
}
